import React, { useState, useEffect, useRef } from 'react';
import './SponsorsPage.css';

const DEFAULT_GLOW_COLOR = '132, 0, 255';

const createParticleElement = (x, y, color = DEFAULT_GLOW_COLOR) => {
  const el = document.createElement('div');
  el.className = 'particle';
  el.style.cssText = `
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(${color}, 1);
    box-shadow: 0 0 6px rgba(${color}, 0.6);
    pointer-events: none;
    z-index: 100;
    left: ${x}px;
    top: ${y}px;
  `;
  return el;
};

const ParticleCard = ({ children, className = '', style }) => {
  const cardRef = useRef(null);
  const particlesRef = useRef([]);
  const timeoutsRef = useRef([]);
  const isHoveredRef = useRef(false);

  const clearAllParticles = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    particlesRef.current.forEach(particle => {
      particle.parentNode?.removeChild(particle);
    });
    particlesRef.current = [];
  };

  const animateParticles = () => {
    if (!cardRef.current || !isHoveredRef.current) return;

    const { width, height } = cardRef.current.getBoundingClientRect();
    
    for (let i = 0; i < 12; i++) {
      const timeoutId = setTimeout(() => {
        if (!isHoveredRef.current || !cardRef.current) return;

        const particle = createParticleElement(
          Math.random() * width,
          Math.random() * height
        );
        
        cardRef.current.appendChild(particle);
        particlesRef.current.push(particle);

        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 2000);
      }, i * 80);

      timeoutsRef.current.push(timeoutId);
    }
  };

  useEffect(() => {
    if (!cardRef.current) return;

    const element = cardRef.current;

    const handleMouseEnter = () => {
      isHoveredRef.current = true;
      animateParticles();
    };

    const handleMouseLeave = () => {
      isHoveredRef.current = false;
      clearAllParticles();
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      isHoveredRef.current = false;
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      clearAllParticles();
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`${className} particle-container`}
      style={{ ...style, position: 'relative', overflow: 'hidden' }}
    >
      {children}
    </div>
  );
};

const FloatingOrbs = () => {
  return (
    <div className="floating-orbs">
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>
      <div className="orb orb-3"></div>
      <div className="orb orb-4"></div>
    </div>
  );
};

const SponsorsPage = () => {
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(false);
    
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const sponsors = {
    associate: [
      { id: 1, name: 'Malabar Gold & Diamonds', logo: '/sponsors/Malabar.jpg', location: 'https://maps.app.goo.gl/KA2SJ67Rm7bvkHY59?g_st=aw' },
      { id: 2, name: 'Oram Salt', logo: '/sponsors/Oram Salt.png', instagram: 'https://www.instagram.com/oramsalt?igsh=MWpyYXBoOGl5OG1wMw==' },
      { id: 3, name: 'Shree Engineers and Builders', logo: '/sponsors/shree.jpeg', location: 'https://maps.app.goo.gl/tR2hpLacExAPcqkEA?g_st=aw' }
    ],
    coSponsor: [
      { id: 5, name: "Hawaii'n Delight", logo: "/sponsors/Hawaii'DelightLogo_page-0001.jpg", location: 'https://maps.google.com/?cid=12864983131074502904&entry=gps&g_st=aw', instagram: 'https://www.instagram.com/hawaii_n_delight?igsh=MWp1ZW5tdDZwbnR4MQ==' }
    ],
    partner: [
      { id: 4, name: 'Sriram Jewellers', logo: '/sponsors/sriram.jpeg', location: 'https://maps.app.goo.gl/GRkp1vGurDoTPdw6A?g_st=aw' }
    ]
  };

  const openImagePopup = (sponsor) => {
    setSelectedImage(sponsor);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className="sponsors-section">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="sponsors-section">
      <FloatingOrbs />
      <div className="container">
        <div className={`sponsors-wrapper ${isVisible ? 'fade-in' : ''}`}>
          <div className="title-wrapper">
            <div className="title-glow"></div>
            <h2 className="section-title">
              <span className="title-word">Our</span>
              <span className="title-word highlight">Sponsors</span>
            </h2>
            <div className="title-underline"></div>
          </div>

          <div className="sponsor-category">
            <h3 className="category-title">
              Co-Sponsor
            </h3>
            <div className="partner-sponsor-grid">
              {sponsors.coSponsor.map((sponsor, index) => (
                <ParticleCard
                  key={sponsor.id}
                  className={`magic-bento-card partner-bento-card animate-card`}
                  style={{
                    backgroundColor: '#060010',
                    border: '2px solid rgba(0, 255, 127, 0.4)',
                    animationDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="sponsor-content">
                    <div className="sponsor-logo-wrapper" onClick={() => openImagePopup(sponsor)}>
                      <img src={sponsor.logo} alt={sponsor.name} className="sponsor-logo" />
                    </div>
                    <h4 className="sponsor-name">{sponsor.name}</h4>
                    <div className="sponsor-buttons">
                      {sponsor.location && (
                        <button 
                          className="location-btn" 
                          onClick={() => window.open(sponsor.location, '_blank')}
                        >
                          <i className="fas fa-map-marker-alt"></i>
                        </button>
                      )}
                      {sponsor.instagram && (
                        <button 
                          className="instagram-btn" 
                          onClick={() => window.open(sponsor.instagram, '_blank')}
                        >
                          <i className="fab fa-instagram"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </ParticleCard>
              ))}
            </div>
          </div>

          <div className="sponsor-category">
            <h3 className="category-title">
              Associate Sponsors
            </h3>
            <div className="magic-bento-grid">
              {sponsors.associate.map((sponsor, index) => (
                <ParticleCard
                  key={sponsor.id}
                  className={`magic-bento-card sponsor-bento-card animate-card`}
                  style={{
                    backgroundColor: '#060010',
                    border: '1px solid rgba(132, 0, 255, 0.3)',
                    animationDelay: `${(sponsors.coSponsor.length + index) * 0.2}s`
                  }}
                >
                  <div className="sponsor-content">
                    <div className="sponsor-logo-wrapper" onClick={() => openImagePopup(sponsor)}>
                      <img src={sponsor.logo} alt={sponsor.name} className="sponsor-logo" />
                    </div>
                    <h4 className="sponsor-name">{sponsor.name}</h4>
                    <div className="sponsor-buttons">
                      {sponsor.location && (
                        <button 
                          className="location-btn" 
                          onClick={() => window.open(sponsor.location, '_blank')}
                        >
                          <i className="fas fa-map-marker-alt"></i>
                        </button>
                      )}
                      {sponsor.instagram && (
                        <button 
                          className="instagram-btn" 
                          onClick={() => window.open(sponsor.instagram, '_blank')}
                        >
                          <i className="fab fa-instagram"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </ParticleCard>
              ))}
            </div>
          </div>

          <div className="sponsor-category">
            <h3 className="category-title premium-title">
              Bronze Sponsor
            </h3>
            <div className="partner-sponsor-grid">
              {sponsors.partner.map((sponsor, index) => (
                <ParticleCard
                  key={sponsor.id}
                  className={`magic-bento-card partner-bento-card animate-card`}
                  style={{
                    backgroundColor: '#060010',
                    border: '2px solid rgba(255, 215, 0, 0.4)',
                    animationDelay: `${(sponsors.associate.length + index) * 0.2}s`
                  }}
                >
                  <div className="sponsor-content">
                    <div className="sponsor-logo-wrapper" onClick={() => openImagePopup(sponsor)}>
                      <img src={sponsor.logo} alt={sponsor.name} className="sponsor-logo" />
                    </div>
                    <h4 className="sponsor-name">{sponsor.name}</h4>
                    <div className="sponsor-buttons">
                      {sponsor.location && (
                        <button 
                          className="location-btn" 
                          onClick={() => window.open(sponsor.location, '_blank')}
                        >
                          <i className="fas fa-map-marker-alt"></i>
                        </button>
                      )}
                      {sponsor.instagram && (
                        <button 
                          className="instagram-btn" 
                          onClick={() => window.open(sponsor.instagram, '_blank')}
                        >
                          <i className="fab fa-instagram"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-shine"></div>
                </ParticleCard>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {selectedImage && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={closeImagePopup}>
              <i className="fas fa-times"></i>
            </button>
            <img src={selectedImage.logo} alt={selectedImage.name} className="popup-image" />
            <h3 className="popup-title">{selectedImage.name}</h3>
          </div>
        </div>
      )}
    </section>
  );
};

export default SponsorsPage;